import PdfUpload from '@admin/components/programs/PdfUpload';
import WorkshopFields from '@admin/components/programs/WorkshopFields';
import Autocomplete from '@common/components/autocomplete/Autocomplete';
import WaveSelectField from '@common/components/form/WaveSelectField';
import WaveTextArea from '@common/components/form/WaveTextArea';
import WaveTextField from '@common/components/form/WaveTextField';
import Chisholm from '@common/config/Chisholm';
import TooltipTitle from '@common/config/Tooltip/TooltipTitle';
import TrashIcon from '@common/icons/TrashIcon';
import MetadataService from '@common/services/MetadataService';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import format from 'date-fns/format';
import React from 'react';
import { withStyles } from 'tss-react/mui';
import { WaveDatePicker } from '../../../../common/components/WaveDatePicker';
import { programFormStyles } from './styles';
import VcmUnitAutocomplete from './VcmUnitAutocomplete';

const meta = new MetadataService();

const ProgramForm = ({
  program,
  validationErrors,
  onProgramChange,
  locationOptions,
  domainOptions,
  categoryOptions,
  editEnabled,
  onProgramPdfChange,
  classes,
  vocationalCurrencyUsesUnits
}) => {
  const [userLabel, setUserLabel] = React.useState(
    program.facilitator_full_name
  );

  const addWorkshop = () => {
    const newProgram = { ...program };
    newProgram.workshops.push({ name: '' });
    onProgramChange(newProgram);
  };

  const errorHelpers = (field) => {
    if (validationErrors.hasOwnProperty(field)) {
      return validationErrors[field][0];
    }
    return null;
  };

  const errorStatus = (field) => {
    return validationErrors.hasOwnProperty(field);
  };

  const handleAutocompleteChange = (id, value) => {
    setUserLabel(value);

    let newProgram = { ...program };
    newProgram['facilitator_id'] = id;

    onProgramChange(newProgram);
  };

  const handleDateChange = (fieldName, date) => {
    const convertedDate =
      date == null ? null : format(new Date(date), 'yyyy-MM-dd');
    let newProgram = { ...program };
    newProgram[fieldName] = convertedDate;

    onProgramChange(newProgram);
  };

  const handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    let newProgram = { ...program };
    newProgram[name] = value;

    onProgramChange(newProgram);
  };

  const handleWorkshopChange = (newWorkshop, index) => {
    let newProgram = { ...program };
    newProgram.workshops[index] = newWorkshop;

    onProgramChange(newProgram);
  };

  const makeFieldProps = (field) => {
    return {
      error: errorStatus(field),
      helperText: errorHelpers(field),
      name: field,
      onChange: handleInputChange,
      value: program[field]
    };
  };

  const removeWorkshop = (index) => {
    let newProgram = { ...program };
    newProgram.workshops[index]._destroy = true;
    onProgramChange(newProgram);
  };

  const renderWorkshops = () => {
    var workshopNumber = 0;
    return program.workshops.map((workshop, index) => {
      if (workshop.hasOwnProperty('_destroy') && workshop._destroy) {
        return null;
      }

      workshopNumber++;

      // validationErrors exists?
      // Trying to access workshop in array doesnt exist?
      // --> Always returns empty object
      const getValidationErrors = validationErrors;
      const workshopValidationErrors = getValidationErrors.hasOwnProperty(
        'workshops'
      )
        ? getValidationErrors.workshops[index] || {}
        : {};

      return (
        <div className={classes.workshopContainer} key={index + 1}>
          <Typography className={classes.sectionHeaders}>
            Workshop {workshopNumber}
            <a
              onClick={() => {
                removeWorkshop(index);
              }}
            >
              <TrashIcon className={classes.deleteWorkshopIcon} />
            </a>
          </Typography>
          <div>
            <WorkshopFields
              locationOptions={locationOptions}
              onWorkshopChange={(workshop) =>
                handleWorkshopChange(workshop, index)
              }
              validationErrors={workshopValidationErrors}
              workshop={workshop}
            />
          </div>
          <Divider />
        </div>
      );
    });
  };

  const getFields = meta.get('programs');
  const programTypeFields = getFields.program_type.options;
  const registrationsOpenOptions = getFields.registrations_open.options;

  if (program == null) {
    return <div>No Program</div>;
  }

  const isVocationalProgram = [
    'vocational_learning_journey',
    'vocational_elective'
  ].includes(program.program_type);

  const shouldHideDomain = isVocationalProgram;
  const shouldShowVcmUnitPicker =
    isVocationalProgram && vocationalCurrencyUsesUnits;

  const shouldShowProgramSummaryDescription =
    makeFieldProps('summary_description').value;

  return (
    <Paper className={classes.programFormContainer}>
      <Typography className={classes.sectionHeaders}>
        Program Details
      </Typography>
      <form noValidate autoComplete="off">
        <Grid container className={classes.formContainer}>
          <Grid size={6} className={classes.formLeft}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <WaveTextField
                  label="Program Name"
                  maxLength={150}
                  {...makeFieldProps('name')}
                />
              </Grid>
              <Grid size={6}>
                <WaveTextField
                  label="Reporting Code"
                  {...makeFieldProps('code')}
                />
              </Grid>

              <Grid size={6}>
                <WaveSelectField
                  label="Program Type"
                  menuItems={programTypeFields}
                  {...makeFieldProps('program_type')}
                />
              </Grid>
              {shouldHideDomain ? null : (
                <Grid size={6}>
                  <WaveSelectField
                    emptyMenuItem
                    label="Domain"
                    menuItems={domainOptions}
                    {...makeFieldProps('domain_id')}
                    error={errorStatus('domain')}
                    helperText={errorHelpers('domain')}
                  />
                </Grid>
              )}
              <Grid size={6}>
                <WaveSelectField
                  emptyMenuItem
                  emptyMenuItemLabel="None"
                  label="Category"
                  menuItems={categoryOptions}
                  {...makeFieldProps('category_id')}
                  error={errorStatus('category')}
                  helperText={errorHelpers('category')}
                />
              </Grid>
              <Grid size={6}>
                <WaveDatePicker
                  className={[
                    errorStatus('start_date') ? '' : classes.errorSpacing,
                    classes.ieFix
                  ].join(' ')}
                  error={errorStatus('start_date')}
                  format={Chisholm.datePicker}
                  helperText={errorHelpers('start_date')}
                  label="Program Start Date"
                  name="start_date"
                  onChange={(value) => handleDateChange('start_date', value)}
                  value={program.start_date}
                />
              </Grid>
              <Grid size={6}>
                <Autocomplete
                  error={errorStatus('facilitator')}
                  errorSpacing={classes.errorSpacing}
                  helperText={errorHelpers('facilitator')}
                  label="Facilitator"
                  name="facilitator_id"
                  onChange={handleAutocompleteChange}
                  role={'facilitator'}
                  tooltipProps={{
                    placement: 'top',
                    titleComponent: (
                      <Typography className={classes.tooltipText}>
                        {TooltipTitle.facilitator}
                      </Typography>
                    ),
                    tooltipContainer: classes.tooltipContainer,
                    tooltipPopper: classes.popper
                  }}
                  value={userLabel || ''}
                />
              </Grid>
              <Grid size={6}>
                <WaveTextField
                  label="Maximum Attendance"
                  type="number"
                  min={1}
                  max={1000}
                  {...makeFieldProps('attendee_limit')}
                />
              </Grid>
              <Grid size={6}>
                <WaveSelectField
                  label="Registrations Open"
                  menuItems={registrationsOpenOptions}
                  {...makeFieldProps('registrations_open')}
                />
              </Grid>
              <Grid size={6}>
                <WaveTextField
                  label="Registration Cutoff (days)"
                  type="number"
                  min={1}
                  max={28}
                  tooltipProps={{
                    placement: 'top',
                    titleComponent: (
                      <Typography className={classes.tooltipText}>
                        Number of days before the program start date that an educator cannot register.
                        Defaults to the institute setting.
                      </Typography>
                    ),
                    tooltipContainer: classes.tooltipContainer,
                    tooltipPopper: classes.popper
                  }}
                  {...makeFieldProps('registration_cutoff_days')}
                />
              </Grid>
              {shouldShowVcmUnitPicker ? (
                <Grid size={12}>
                  <VcmUnitAutocomplete
                    label="Related to Units"
                    placeholder="Search by unit code"
                    initialUnits={program.units || []}
                    {...makeFieldProps(`unit_codes`)}
                  />
                </Grid>
              ) : null}
            </Grid>
          </Grid>
          <Grid size={6} className={classes.formRight}>
            <Grid container className={classes.textAreaContainer} spacing={3}>
              <Grid size={12}>
                <WaveTextArea
                  label="Program Description"
                  maxLength={4000}
                  rows={6}
                  {...makeFieldProps('description')}
                />
              </Grid>
              {shouldShowProgramSummaryDescription ? (<Grid size={12}>
                <WaveTextArea
                  label="Summary Description"
                  maxLength={1000}
                  rows={6}
                  tooltipProps={{
                    placement: 'top',
                    titleComponent: (
                      <Typography className={classes.tooltipText}>
                        {TooltipTitle.summaryDescription}
                      </Typography>
                    ),
                    tooltipContainer: classes.tooltipContainer,
                    tooltipPopper: classes.popper
                  }}
                  {...makeFieldProps('summary_description')}
                />
              </Grid>
            ) : null}
            </Grid>
          </Grid>
        </Grid>
        <Divider />

        {renderWorkshops()}

        {editEnabled && (
          <Button
            className={classes.addWorkshopBtn}
            color="secondary"
            onClick={addWorkshop}
            variant="contained"
          >
            <Typography variant="button">Add Workshop</Typography>
          </Button>
        )}

        <Typography className={classes.sectionHeaders}>
          Contact Information
        </Typography>

        <Grid container spacing={5} mb={2}>
          <Grid size={3}>
            <WaveTextField
              label="Contact Name"
              {...makeFieldProps('contact_name')}
            />
          </Grid>
          <Grid size={3}>
            <WaveTextField
              label="Job Title or Description"
              {...makeFieldProps('contact_title')}
            />
          </Grid>
          <Grid size={3}>
            <WaveTextField
              label="Email"
              type="email"
              {...makeFieldProps('contact_email')}
            />
          </Grid>
          <Grid size={3}>
            <WaveTextField
              label="Phone Number or Extension"
              {...makeFieldProps('contact_phone')}
            />
          </Grid>
        </Grid>

        <Grid container>
          <Grid size={12}>
            <Typography className={classes.sectionHeaders}>
              Upload PDF (max 10MB)
            </Typography>

            <PdfUpload
              program={program}
              onProgramPdfChange={onProgramPdfChange}
              editEnabled={editEnabled}
              fieldErrorMessage={errorHelpers('pdf_filename')}
            />
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

const StyledProgramForm = withStyles(ProgramForm, programFormStyles, {
  withTheme: true
});

export { StyledProgramForm as ProgramForm };
